import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import vanilla from '../Vanilla.mp4';
import gsdiagram from '../gs-diagram.svg';
import conversation from '../conversation.png';
import audit from '../audit.png';
import gsParts from '../gs-parts.png';
import orderticketphoto from '../order-ticket-photo.png';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import './CaseStudy.css';

function CaseStudy4() {
  const [videoRef, isVisible] = useIntersectionObserver({ threshold: 0.1 });

  useEffect(() => {
    if (isVisible && videoRef.current) {
      videoRef.current.play();
    }
  }, [isVisible, videoRef]);

  return (
    <div className="case-study-page">
      <div className="case-study-container">
        <Link to="/" className="back-button">
          <i className="fas fa-arrow-left"></i>
          <span>Back to all Projects</span>
        </Link>
        
        <div className="case-study-headline">
          <h1>Reducing execution errors for Wall Street Floor Brokers through an IA system re-design of order tickets.</h1>
          <p className="internal-case-study-body">Banking has traditionally been one of the least digital industries, with Wall Street being slower than commercial banking to embrace digital innovation.</p>
        </div>
        
        <div className="case-study-section">
          <div className="case-study-left-column">
            <div className="info-pair">
              <span className="info-label">Timeline</span>
              <span className="info-text">2019</span>
            </div>
            <div className="info-pair">
              <span className="info-label">ICP Targeted</span>
              <span className="info-text">Floor Brokers</span>
            </div>
            <div className="info-pair">
              <span className="info-label">My Role</span>
              <span className="info-text">Product Designer</span>
            </div>
          </div>
          
          <div className="case-study-right-column">
            <div className="section-content">
              <span className="info-label">Background</span>
              <h2>The Problem</h2>
              <p className="internal-case-study-body">Wall Street floor brokers were experiencing frequent execution errors due to outdated, manual order ticket systems. The existing interface was prone to user errors, lacked validation, and created significant risk for trading operations and client relationships.</p>
            </div>
          </div>
        </div>
        
        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <video 
            ref={videoRef}
            src={vanilla} 
            controls 
            muted
            style={{ width: '100%', height: 'auto' }}
            alt="Homepage Carousel Demo"
          >
            Your browser does not support the video tag.
          </video>
        </div>


        <div className="case-study-section">
            <div className="section-content">
              <span className="info-label">Problem to Solve</span>
              <h2>Designing across asset classes</h2>
              <p className="internal-case-study-body">Traditionally, trading desks were organized by asset classes such as equities, commodities, and forex. However, as clients and users moved away from this model, the Marquee product team continued to operate around asset classes, with different teams developing separate workflows for each.</p>
              <p className="internal-case-study-body">This created a fragmented user experience: users who traded across multiple asset classes had to navigate distinct interfaces and workflows, leading to confusion and inefficiency. My role was to help the Marquee team step back and create a unified experience across all asset classes, focusing on streamlining execution workflows.</p>
            </div>
        </div>
        
        <div className="case-study-section-slim" style={{ width: '75%', marginTop: '32px' }}>
          <img src={gsdiagram} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="case-study-section">
            <div className="section-content">
              <span className="info-label">Industry Definitions</span>
              <h2>What is an Order Ticket?</h2>
              <p className="internal-case-study-body">Order tickets, sometimes called ‘receipts,’ are forms where users input trade details like price, volume, and dates. While order tickets exist across asset classes, they were designed differently in Marquee for each one, causing inconsistency and a disjointed user experience.</p>
            </div>
        </div>

        <div style={{ width: '100%', marginTop: '32px', display: 'flex', justifyContent: 'center' }}>
          <img src={orderticketphoto} alt="Case Study 2" className="half-width-image" style={{ height: 'auto' }} />
        </div>

        <div className="case-study-section">
            <div className="section-content">
              <span className="info-label">Process</span>
              <h2>Removing the interface</h2>
              <p className="internal-case-study-body">I separated each order ticket into two layers: the conversational layer, which involves user inputs, and the visual layer, which represents the design. Without a consistent conversational layer, executing trades across asset classes would continue to feel disjointed.</p>
              <p className="internal-case-study-body">Using information architecture (IA) mapping techniques, I visualized the differences in the conversational layer and identified where significant variances occurred. I repeated this process across asset classes and presented the results to the GS design team.</p>
            </div>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={conversation} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>


        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={audit} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="margin-top-72 section-content">
              <span className="info-label">Solution</span>
              <h2>Identifying patterns and creating a system</h2>
              <p className="internal-case-study-body">Through discussions with financial experts at Goldman Sachs, I was able to understand the rationale behind explicit design choices and identify misalignments or miscommunications. I documented a clear structure for all order tickets: Foundation, Parameters, Advanced, and Action.</p>
              <h3>Foundation</h3>
              <p className="internal-case-study-body">Every ticket has two consistent inputs—product type and order type.</p>
              <h3>Parameters</h3>
              <p className="internal-case-study-body">These vary depending on the order type and define the trade characteristics. </p>
              <h3>Advanced</h3>
              <p className="internal-case-study-body">Not all tickets have this section, but it includes additional settings for experienced traders.</p>
              <h3>Action</h3>
              <p className="internal-case-study-body">This is the button that executes the trade.</p>
        </div>


        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <img src={gsParts} alt="Case Study 2" style={{ width: '100%', height: 'auto' }} />
        </div>

        <div className="case-study-section">
            <div className="section-content">
              <span className="info-label">Solution</span>
              <h2>Converging 5 tickets into 1</h2>
              <p className="internal-case-study-body">By applying this new structure, I broke down five different order tickets across asset classes into the same four sections. This revealed that all five tickets shared identical input structures, offering an opportunity to simplify and unify the user experience.</p>
            </div>
        </div>

        <div className="margin-top-72 image-container" style={{ width: '100%'}}>
          <video 
            ref={videoRef}
            src={vanilla} 
            controls 
            muted
            style={{ width: '100%', height: 'auto' }}
            alt="Homepage Carousel Demo"
          >
            Your browser does not support the video tag.
          </video>
        </div>

      </div>
    </div>
  );
}

export default CaseStudy4;

